import { useRef } from "react";
import { IoExpandOutline } from "react-icons/io5";
import autovistaOrders from "./images/autovista-orders.png";
import msBubbleOrder from "./images/ms-bubble-order.png";

const services = [
  {
    title: "Team collaboration hubs",
    desc: "Centralized spaces that connect people, projects, and knowledge in one place.",
    // example:
    //   "A non-profit needed a secure, streamlined way to coordinate care among social workers, volunteers, and family members supporting seniors in the community. We built a cross-platform app (web, iOS, Android) tailored to their operational workflows: task management for assigning responsibilities, journaling and documentation for caregiving team updates, group calendars for scheduling check-ins, resource directories, and permission-based access to ensure data privacy",
  },
  {
    title: "Workflow automation",
    desc: "Tools that eliminate busywork and bring structure to recurring operational tasks, reducing clicks, tabs, logins, and headaches.",
    workTitle: "McCollister's Transportation Group: Autovista Portal",
    images: [
      {
        src: autovistaOrders,
        alt: "Autovista orders list in demo mode, with search, filters, and order status",
      },
    ],
    example: [
      "A transportation and logistics company required a better way to manage their increasingly complex system of quotes, orders, and client accounts, which were updated on a minute-to-minute basis across multiple third party platforms. We created a custom administrative dashboard where employees could manage their workflow with greater ease and simplicity.",
      "Behind the scenes, their new API communicated with the many services they were previously handling with manual input and oversight, and offered public endpoints for clients who wished to use a REST API for their own front end experiences.",
    ],
  },
  {
    title: "Unified dashboards",
    desc: "Single interfaces that process data and actions from tools you already use like HubSpot, Salesforce, Slack, QuickBooks, and more.",
  },
  {
    title: "Reporting tools",
    desc: "Customized data visualization to track sales and revenue, KPIs, team performance, and other operational insights.",
  },
  {
    title: "Complex pricing engines",
    desc: "Tailored quoting systems that handle complex pricing logic and can integrate directly into your sales or fulfillment workflows.",
    workTitle: "Ms Bubble",
    images: [
      {
        src: msBubbleOrder,
        alt: "Ms Bubble ordering screen with laundry services, dry cleaning options, and an order summary",
      },
    ],
    example:
      "A laundromat chain with over 20 locations hired us to build a new web app to centralize their online ordering process, in a more flexible and industry-specific way than their previous off-the-shelf platform. In addition to refreshing the customer ordering experience, we built an administrative portal to handle pricing, quoting, scheduling, and payment processing.",
  },
  {
    title: "Membership platforms",
    desc: "Custom portals for gated content, services, or tools for paying customers or internal teams.",
    // example:
    //   "A professional speaker in the education space hired us to create a member platform where new updates, resources, and events could be posted regularly, with different members tiers receiving different content and interactive opportunities. Our client wanted to be able to fine-tune both the website and content regularly using a familiar interface, so we built a custom theme and series of plugins on top of the WordPress engine to empower him to update content and manage members easily on his own.",
  },
];

function WorkShot({ image }) {
  const dialogRef = useRef(null);

  function open() {
    dialogRef.current?.showModal();
  }

  function close() {
    dialogRef.current?.close();
  }

  return (
    <>
      <button
        type="button"
        className="work-shot"
        onClick={open}
        aria-label={`View larger: ${image.alt}`}
      >
        <img src={image.src} alt="" />
        <span className="work-shot-expand" aria-hidden="true">
          <IoExpandOutline />
        </span>
      </button>
      <dialog
        ref={dialogRef}
        className="work-lightbox"
        aria-label={image.alt}
        onClick={(event) => {
          if (event.target === dialogRef.current) close();
        }}
      >
        <button type="button" className="work-lightbox-close" onClick={close}>
          Close
        </button>
        <img src={image.src} alt={image.alt} />
      </dialog>
    </>
  );
}

function Home() {
  return (
    <div className="home">
      <section className="hero shell">
        <h1>Built to fit and yours to own</h1>
        <div className="hero-copy">
          <p>
            We are a <strong>technology studio</strong> specializing in
            thoughtful, minimalist software that helps your team run its
            operations smoothly, with a gentle learning curve and the
            flexibility to grow.
          </p>
          <p>
            We take a <strong>human-centered</strong> approach to everything
            from systems design to project management, because we believe that
            every puzzle we solve must start with the user in mind.
          </p>
          <p>
            When we build <strong>custom software</strong> for you, it's yours
            to own forever, and we provide you with all source code,
            documentation, and credentials. We can even help onboard your
            in-house development team.
          </p>
        </div>
      </section>
      <section className="expertise shell" id="expertise" aria-labelledby="expertise-title">
        <h2 id="expertise-title">Our expertise</h2>
        <ul className="service-grid">
          {services.map((service, index) => (
            <li key={service.title}>
              <p className="service-index">{String(index + 1).padStart(2, "0")}</p>
              <h3>{service.title}</h3>
              <p>{service.desc}</p>
            </li>
          ))}
        </ul>
        <div className="selected-work">
          <h2>Selected work</h2>
          {services
            .filter((service) => service.example)
            .map((service) => (
              <article key={service.title}>
                <div className="work-copy">
                  <h3>{service.workTitle || service.title}</h3>
                  {(Array.isArray(service.example)
                    ? service.example
                    : [service.example]
                  ).map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
                {service.images?.length > 0 && (
                  <div className="work-shots">
                    {service.images.map((image) => (
                      <WorkShot key={image.alt} image={image} />
                    ))}
                  </div>
                )}
              </article>
            ))}
        </div>
      </section>
    </div>
  );
}

export default Home;
